/**
 * Real-time curtailment card — only when confidence ≥ medium and wasted ≥ threshold (Part 3).
 * Calm, informational; kWh + confidence shown. No energy math in UI (values from backend).
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sun, Zap } from 'lucide-react';
import { PvCurtailmentTooltip } from '@/components/dashboard/command-center/PvCurtailmentTooltip';
import { confidenceAtLeastMedium } from '@/hooks/useCurtailment';
import { t } from '@/lib/vocabulary';
import type { CurtailmentStatus } from '@/types/dashboard';

/** Minimum wasted power (W) or today (kWh) to show card. */
const WASTED_POWER_THRESHOLD_W = 100;
const WASTED_TODAY_THRESHOLD_KWH = 0.1;

/** Format curtailment dollar value with precision based on magnitude. */
export function formatCurtailmentDollars(dollars: number): string {
  if (dollars <= 0) return '$0';
  if (dollars < 1) return `${(dollars * 100).toFixed(0)}\u00A2`; // e.g. "50¢"
  if (dollars >= 100) return `$${Math.round(dollars).toLocaleString()}`;
  return `$${dollars.toFixed(2)}`;
}

function shouldShowCard(status: CurtailmentStatus | null): boolean {
  if (!status) return false;
  if (!confidenceAtLeastMedium(status.confidence)) return false;
  if (status.wasted_power_w >= WASTED_POWER_THRESHOLD_W) return true;
  if (status.wasted_today_kwh >= WASTED_TODAY_THRESHOLD_KWH) return true;
  if (status.is_curtailing_now && (status.wasted_power_w > 0 || status.wasted_today_kwh > 0)) return true;
  return false;
}

function confidenceLabel(confidence: string): string {
  const labels: Record<string, string> = {
    very_high: 'Very high',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    very_low: 'Very low',
  };
  return labels[confidence] ?? confidence;
}

interface CurtailmentCardProps {
  status: CurtailmentStatus | null;
  loading?: boolean;
  error?: string | null;
  isHomeowner?: boolean;
  rateCentsPerKwh?: number;
}

export function CurtailmentCard({ status, loading = false, error = null, isHomeowner = false, rateCentsPerKwh }: CurtailmentCardProps) {
  if (loading && !status) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm flex items-center gap-1.5 font-medium text-muted-foreground">
            <Sun className="h-4 w-4" />
            {t('curtailment', isHomeowner)}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="h-12 rounded bg-muted/30 animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-medium text-muted-foreground">{t('curtailment', isHomeowner)}</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <p className="text-xs text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!shouldShowCard(status)) return null;

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-1.5 font-medium text-muted-foreground">
            <Sun className="h-4 w-4" />
            {t('curtailment', isHomeowner)}
            <PvCurtailmentTooltip />
          </CardTitle>
          {status && (
            <Badge variant="outline" className="text-xs text-muted-foreground border-muted">
              {confidenceLabel(status.confidence)}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-2">
        {status?.is_curtailing_now && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-wt-amber-primary" />
            Solar is being limited now (batteries full, low load).
          </p>
        )}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {status && status.wasted_power_w > 0 && (
            <div>
              <span className="text-muted-foreground">Right now</span>
              <p className="font-medium tabular-nums">{status.wasted_power_w.toFixed(0)} W</p>
            </div>
          )}
          {status && status.wasted_today_kwh > 0 && (
            <div>
              <span className="text-muted-foreground">Today</span>
              <p className="font-medium tabular-nums">{status.wasted_today_kwh.toFixed(2)} kWh</p>
              {rateCentsPerKwh != null && rateCentsPerKwh > 0 && (
                <p className="text-muted-foreground tabular-nums">
                  {formatCurtailmentDollars(status.wasted_today_kwh * rateCentsPerKwh / 100)} curtailed
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
